import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { StatusChip, documentStatusTone } from './StatusChip';
import { pipelineStageKey } from './pollGuard';

const PHASES = [
  ['QuarantineAndPromote', 'UnpackBundle'],
  ['ExtractText', 'DetectTemplate', 'ExtractFields', 'Classify'],
  ['ResolveProject', 'ConflictCheck', 'ReadyForReview'],
];
const STAGES = PHASES.flat();

export function pipelineState(jobStatus?: string | null, documentStatus?: string | null) {
  const failed = ['Failed', 'DeadLettered'].includes(jobStatus ?? '')
    || ['Failed', 'DeadLettered', 'Rejected', 'Unreadable'].includes(documentStatus ?? '');
  const complete = !failed && (jobStatus === 'Succeeded'
    || (!jobStatus && ['Verified', 'NeedsReview', 'Duplicate', 'Unrecognised'].includes(documentStatus ?? '')));
  return { failed, complete };
}

interface PipelineStepperProps {
  currentStage?: string | null;
  jobStatus?: string | null;
  documentStatus?: string | null;
}

const PipelineStepper: React.FC<PipelineStepperProps> = ({ currentStage, jobStatus, documentStatus }) => {
  const { t } = useTranslation();
  const { failed, complete } = pipelineState(jobStatus, documentStatus);
  if (failed || (complete && ['NeedsReview', 'Unrecognised', 'Duplicate'].includes(documentStatus ?? ''))) {
    const status = failed && ['Failed', 'DeadLettered'].includes(jobStatus ?? '')
      ? jobStatus : documentStatus;
    return <Box sx={{ justifySelf: 'start', width: 'fit-content', maxWidth: '100%' }}><StatusChip label={t(`pipeline.results.${status}`, { defaultValue: status ?? '' })} tone={failed ? 'error' : documentStatusTone(documentStatus)} /></Box>;
  }
  if (complete) {
    return (
      <Stack direction="row" spacing={3} alignItems="center" sx={{ color: 'success.main' }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{t(documentStatus === 'Verified' ? 'pipeline.verified' : 'pipeline.complete')}</Typography>
      </Stack>
    );
  }
  const index = STAGES.indexOf(currentStage ?? '');
  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {index < 0 && <Typography sx={{ mb: 3, fontSize: 13 }}>{t('pipeline.queued')}</Typography>}
      <Box component="ol" aria-label={t('upload.pipeline')} sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', md: 'repeat(9, minmax(0, 1fr))' }, gap: 3 }}>
        {STAGES.map((stage, i) => (
          <Box component="li" key={stage} aria-current={i === index ? 'step' : undefined}>
            <Box sx={{ height: 5, mb: 3, borderRadius: 2, bgcolor: i < index ? 'success.main' : i === index ? 'primary.main' : 'action.disabledBackground' }} />
            <Typography sx={{ fontSize: 12, fontWeight: i === index ? 700 : 400, color: i === index ? 'primary.main' : 'text.secondary', overflowWrap: 'anywhere' }}>
              {t(pipelineStageKey(stage))}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PipelineStepper;
